import Vue from 'vue'
import { reactive } from 'vue'
import adminUserApi from '@/api/adminUser'

function getInitialState() {
  return {
    isOilTreatmentEditing: false,
    oilTreatmentInput: null,
    isLaundryTypeEditing: false,
    laundryTypeInput: null,
  }
}

export const useOilLaundryInput = () => {
  const states = reactive({})

  const ensureEntry = (user) => {
    if (!user || !user.id) return null
    if (!states[user.id]) {
      states[user.id] = reactive(getInitialState())
    }
    return states[user.id]
  }

  const getEntryById = (userId) => {
    if (!userId) return null
    return states[userId] || null
  }

  const initializeOilLaundryInputState = (user) => {
    const entry = ensureEntry(user)
    if (!entry) return null
    entry.isOilTreatmentEditing = false
    entry.oilTreatmentInput = null
    entry.isLaundryTypeEditing = false
    entry.laundryTypeInput = null
    return entry
  }

  const enterOilTreatmentInputMode = (user) => {
    const entry = ensureEntry(user)
    if (!entry) return null
    entry.isOilTreatmentEditing = true
    entry.oilTreatmentInput = user.has_oil_treatment === null
      ? ''
      : user.has_oil_treatment.toString()
    return entry
  }

  const exitOilTreatmentInputMode = (user) => {
    const entry = ensureEntry(user)
    if (!entry) return null
    entry.isOilTreatmentEditing = false
    entry.oilTreatmentInput = null
    return entry
  }

  const enterLaundryTypeInputMode = (user) => {
    const entry = ensureEntry(user)
    if (!entry) return null
    entry.isLaundryTypeEditing = true
    entry.laundryTypeInput = user.laundry_type === null
      ? ''
      : user.laundry_type.toString()
    return entry
  }

  const exitLaundryTypeInputMode = (user) => {
    const entry = ensureEntry(user)
    if (!entry) return null
    entry.isLaundryTypeEditing = false
    entry.laundryTypeInput = null
    return entry
  }

  const persistOilTreatment = async (users, user) => {
    const entry = ensureEntry(user)
    if (!entry) return
    if (entry.oilTreatmentInput !== '0' && entry.oilTreatmentInput !== '1') {
      console.error('has_oil_treatment must be "0" or "1"')
      return
    }
    const obj = {
      has_oil_treatment: Number(entry.oilTreatmentInput),
    }
    const original = user.has_oil_treatment
    try {
      await adminUserApi.updateHasOilTreatment(user.id, obj)
      user.has_oil_treatment = obj.has_oil_treatment
      const idx = (users || []).findIndex((candidate) => candidate && candidate.id === user.id)
      if (idx !== -1) {
        Vue.set(users, idx, user)
      }
      exitOilTreatmentInputMode(user)
    } catch (e) {
      console.error('Failed to update has_oil_treatment', e)
      user.has_oil_treatment = original
    }
  }

  const persistLaundryType = async (users, user) => {
    const entry = ensureEntry(user)
    if (!entry) return
    if (!entry.laundryTypeInput) {
      console.error('laundry_type is required')
      return
    }
    const parsedLaundryType = Number(entry.laundryTypeInput)
    if (Number.isNaN(parsedLaundryType)) {
      console.error('laundry_type must be numeric')
      return
    }
    const obj = {
      laundry_type: parsedLaundryType,
    }
    const original = user.laundry_type
    try {
      await adminUserApi.updateLaundryType(user.id, obj)
      user.laundry_type = obj.laundry_type
      const idx = (users || []).findIndex((candidate) => candidate && candidate.id === user.id)
      if (idx !== -1) {
        Vue.set(users, idx, user)
      }
      exitLaundryTypeInputMode(user)
    } catch (e) {
      console.error('Failed to update laundry_type', e)
      user.laundry_type = original
    }
  }

  const shouldShowOilTreatmentDisplay = (userId) => {
    const entry = getEntryById(userId)
    return !!entry && !entry.isOilTreatmentEditing
  }

  const shouldShowOilTreatmentEditor = (userId) => {
    const entry = getEntryById(userId)
    return !!entry && entry.isOilTreatmentEditing
  }

  const shouldShowLaundryTypeDisplay = (userId) => {
    const entry = getEntryById(userId)
    return !!entry && !entry.isLaundryTypeEditing
  }

  const shouldShowLaundryTypeEditor = (userId) => {
    const entry = getEntryById(userId)
    return !!entry && entry.isLaundryTypeEditing
  }

  return {
    states,
    initializeOilLaundryInputState,
    enterOilTreatmentInputMode,
    exitOilTreatmentInputMode,
    enterLaundryTypeInputMode,
    exitLaundryTypeInputMode,
    persistOilTreatment,
    persistLaundryType,
    shouldShowOilTreatmentDisplay,
    shouldShowOilTreatmentEditor,
    shouldShowLaundryTypeDisplay,
    shouldShowLaundryTypeEditor,
  }
}
